// src/pages/SupplyChainDetail.jsx
//
// Per-supplier detail dashboard.
//
// Top of page:
//   1. Thin back bar (own row, sticky)
//   2. Supplier header (name + score + meta + date-range)
// Below:
//   - Identity Threat Index panel (gauge + 12-month line chart)
//   - Three risk-category rows (Malware / Breaches / Combolists)
//   - Recent Identity Exposure Events table
//   - Password Reuse panel
//   - Top Compromised Applications panel

import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  ArrowUpRight,
  ArrowDownRight,
  ChevronDown,
  Info,
} from "lucide-react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";
import Sidebar from "../components/Sidebar";
import { useSidebarCollapsed } from "../utils/useSidebarCollapsed";
import { FLAG, COUNTRY_OPTIONS, loadCompanies } from "../data/supplyChainCompanies";

/* ────────────────────────── Visual tokens ────────────────────────── */

const panel = "rounded-md border border-sky-900/30 bg-[#0a1224]/70";
const panelHeader = "px-4 py-3 border-b border-sky-900/30 flex items-center justify-between gap-3";
const panelTitle = "text-[13px] font-semibold text-neutral-100";

/* ────────────────────────── Score helpers ────────────────────────── */

function scoreTone(score) {
  if (score >= 75) return { ring: "#f43f5e", text: "text-rose-300" };
  if (score >= 50) return { ring: "#f59e0b", text: "text-amber-300" };
  if (score >= 25) return { ring: "#eab308", text: "text-yellow-300" };
  return { ring: "#10b981", text: "text-emerald-300" };
}

function Gauge({ score, size = "md", label }) {
  const dim = size === "lg" ? 128 : size === "md" ? 72 : 28;
  const stroke = size === "lg" ? 6 : size === "md" ? 5 : 3;
  const fontSize = size === "lg" ? 28 : size === "md" ? 18 : 10;
  const labelSize = size === "lg" ? 11 : 9;
  const cx = dim / 2;
  const r = (dim - stroke) / 2;
  const c = 2 * Math.PI * r;
  const offset = c - (Math.max(0, Math.min(100, score)) / 100) * c;
  const { ring, text } = scoreTone(score);
  return (
    <div className="relative shrink-0" style={{ width: dim, height: dim }}>
      <svg viewBox={`0 0 ${dim} ${dim}`} className="-rotate-90" width={dim} height={dim}>
        <circle cx={cx} cy={cx} r={r} fill="none" stroke="rgba(148,163,184,0.15)" strokeWidth={stroke} />
        <circle
          cx={cx}
          cy={cx}
          r={r}
          fill="none"
          stroke={ring}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={offset}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center leading-tight">
        <div className={`font-bold ${text}`} style={{ fontSize }}>
          {score}
        </div>
        {label && size !== "sm" && (
          <div className="text-neutral-500 mt-0.5 text-center" style={{ fontSize: labelSize }}>
            {label}
          </div>
        )}
      </div>
    </div>
  );
}

function CompanyAvatar({ name, size = 56 }) {
  const safeName = String(name || "");
  const hash = Array.from(safeName).reduce((a, c) => a + c.charCodeAt(0), 0);
  const palette = [
    "from-sky-500/30 to-sky-700/30 text-sky-200 border-sky-700/40",
    "from-violet-500/30 to-violet-700/30 text-violet-200 border-violet-700/40",
    "from-emerald-500/30 to-emerald-700/30 text-emerald-200 border-emerald-700/40",
    "from-rose-500/30 to-rose-700/30 text-rose-200 border-rose-700/40",
    "from-amber-500/30 to-amber-700/30 text-amber-200 border-amber-700/40",
    "from-cyan-500/30 to-cyan-700/30 text-cyan-200 border-cyan-700/40",
  ];
  const cls = palette[hash % palette.length];
  const initial = safeName.replace(/[^A-Za-z]/g, "").slice(0, 1).toUpperCase() || "?";
  return (
    <div
      className={`shrink-0 rounded-lg border bg-gradient-to-br ${cls} flex items-center justify-center font-semibold`}
      style={{ width: size, height: size, fontSize: size / 2.5 }}
    >
      {initial}
    </div>
  );
}

/* ────────────────────────── Mock data generator ──────────────────────────
 *
 * Derives a plausible vendor-risk dataset from a company's existing index
 * scores. Same input always returns the same numbers.
 */
function generateDetailData(company) {
  const seed = Array.from(company.domain).reduce((a, c) => a + c.charCodeAt(0), 0);
  const rand = (offset) => ((seed * 9301 + offset * 49297) % 233280) / 233280;
  const scale = Math.max(0.1, company.identity / 50);
  const round = (n) => Math.max(0, Math.round(n));

  const malware = {
    index: company.malware.v,
    totalSources: round(70 * scale * (0.7 + rand(1) * 0.6)),
    mostRecentInfection: company.malware.d,
    frequency: round(79 * scale),
    infectedDevices: round(950 * scale * (0.6 + rand(2) * 0.8)),
    infectedEmployees: round(1013 * scale * (0.6 + rand(3) * 0.8)),
    compromisedApps: round(2824 * scale * (0.6 + rand(4) * 0.8)),
  };
  const breaches = {
    index: company.breach.v,
    totalSources: round(106 * scale * (0.7 + rand(5) * 0.6)),
    mostRecentExposure: company.breach.d,
    frequency: round(2526 * scale),
    exposedRecords: round(30731 * scale * (0.5 + rand(6) * 1.0)),
    exposedEmployees: round(13253 * scale * (0.5 + rand(7) * 1.0)),
    plaintextPasswords: round(20956 * scale * (0.5 + rand(8) * 1.0)),
  };
  const combolists = {
    index: company.combo.v,
    totalSources: round(35 * scale * (0.7 + rand(9) * 0.6)),
    mostRecentExposure: company.combo.d,
    frequency: round(1070 * scale),
    exposedRecords: round(13024 * scale * (0.5 + rand(10) * 1.0)),
    exposedEmployees: round(7366 * scale * (0.5 + rand(11) * 1.0)),
    plaintextPasswords: round(13000 * scale * (0.5 + rand(12) * 1.0)),
  };
  const months = ["Apr 25", "May 25", "Jun 25", "Jul 25", "Aug 25", "Sep 25", "Oct 25", "Nov 25", "Dec 25", "Jan 26", "Feb 26", "Mar 26"];
  const trend = months.map((m, i) => ({
    month: m,
    malware: round(15 + 35 * rand(40 + i)),
    breach: round(20 + 40 * rand(60 + i) + (i === 9 ? 30 : 0)),
    combo: round(10 + 25 * rand(80 + i)),
  }));

  const sourceCatalog = [
    { name: "Business Contact Database", type: "Breach" },
    { name: "Russian Regional Web Portals", type: "Breach" },
    { name: "Trackitt", type: "Breach" },
    { name: "Investment Dojo", type: "Breach" },
    { name: "Email Marketing List Collection", type: "Breach" },
    { name: "ROMWE.com", type: "Breach" },
    { name: "Crunchbase", type: "Breach" },
    { name: "ACC Limited", type: "Breach" },
    { name: "Vidar Stealer", type: "Malware" },
    { name: "RedLine Stealer", type: "Malware" },
  ];
  const recentEvents = sourceCatalog.map((s, i) => ({
    ...s,
    date: new Date(Date.now() - i * 86400000 * (1 + Math.floor(rand(100 + i) * 3))).toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    }),
    employees: round(1 + 20 * rand(200 + i)),
  }));

  const reusePct = Math.min(98, round(50 + 50 * (company.identity / 100)));
  const topReusedLeft = Array.from({ length: 5 }, (_, i) => ({
    masked: "••••••••",
    sightings: round(35 - i * 4 - rand(300 + i) * 2),
    users: Math.max(1, round((4 - i) * (1 + rand(310 + i)))),
  }));
  const topReusedRight = Array.from({ length: 5 }, (_, i) => ({
    masked: "••••••••",
    sightings: round(16 - i * 1.5),
    users: Math.max(1, round((3 - i) * 0.7 + rand(320 + i) * 2)),
  }));

  const domainRoot = company.domain.split(".")[0];
  const internalApps = [
    { name: `adfs.${company.domain}`, infected: round(4 + rand(400) * 3) },
    { name: `mylogin.${company.domain}`, infected: round(3 + rand(401) * 3) },
    { name: `securelogin.${company.domain}`, infected: round(2 + rand(402) * 2) },
    { name: `myconnect.${company.domain}`, infected: 1 },
  ];
  const externalApps = [
    { name: `${domainRoot.slice(0, 4)}r.service-now.com`, infected: round(2 + rand(410) * 2) },
    { name: `${domainRoot.slice(0, 4)}r.wd1.myworkdayjobs.com`, infected: round(1 + rand(411) * 1.5) },
    { name: `${domainRoot}.atlassian.net`, infected: 1 },
    { name: `${domainRoot}-corp.okta.com`, infected: 1 },
  ];

  const lower = company.name.toLowerCase();
  let industry = "Software & SaaS";
  if (/health|ehr|patient|telehealth|lab|clinic|medical/.test(lower)) industry = "Healthcare";
  else if (/pay|stripe|bank|cycle|revenue|invoice/.test(lower)) industry = "Financial Services";
  else if (/network|defense|access|cloud|security|identity|risk/.test(lower)) industry = "Cybersecurity";
  else if (/data|monitor|datadog|log|message/.test(lower)) industry = "Developer Infrastructure";
  else if (/regulatory|edi|vendor|portal/.test(lower)) industry = "Supply Chain & Compliance";

  const hqLocation =
    COUNTRY_OPTIONS.find((c) => c.code === company.country)?.label || company.country;

  return {
    ...company,
    industry,
    hqLocation,
    domainsCount: 1 + Math.floor(rand(500) * 3),
    malware,
    breaches,
    combolists,
    trend,
    recentEvents,
    passwordReuse: { percent: reusePct, leftColumn: topReusedLeft, rightColumn: topReusedRight },
    topCompromisedApps: { internal: internalApps, external: externalApps },
    totalSources: malware.totalSources + breaches.totalSources + combolists.totalSources,
    mostRecentSighting: company.malware.d,
  };
}

/* ────────────────────────── Sub-components ────────────────────────── */

function MetaItem({ label, value, valueExtra }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-[12px]">
      <span className="text-neutral-500">{label}:</span>
      <span className="text-neutral-200 font-medium">{value}</span>
      {valueExtra}
    </span>
  );
}

function IdentityThreatPanel({ data }) {
  const [signalsOn, setSignalsOn] = useState(true);
  const [signal, setSignal] = useState("malware");
  const [signalMenuOpen, setSignalMenuOpen] = useState(false);

  const SIGNALS = [
    { key: "malware", label: "Malware", color: "#f43f5e" },
    { key: "breach", label: "Breach", color: "#f59e0b" },
    { key: "combo", label: "Combolists", color: "#eab308" },
  ];
  const active = SIGNALS.find((s) => s.key === signal);
  // Gauge tracks the currently selected signal so the visual matches the
  // dropdown label (previously the gauge was hard-coded to phish.index).
  const signalScore = {
    malware: data?.malware?.index ?? 0,
    breach: data?.breaches?.index ?? 0,
    combo: data?.combolists?.index ?? 0,
  }[signal];

  return (
    <section className={`${panel} h-full flex flex-col`}>
      <div className={panelHeader}>
        <div className={panelTitle}>Identity Threat Index</div>
        <div className="flex items-center gap-3">
          <label className="inline-flex items-center gap-1.5 text-[12px] text-neutral-300 cursor-pointer">
            <input
              type="checkbox"
              checked={signalsOn}
              onChange={(e) => setSignalsOn(e.target.checked)}
              className="accent-sky-500"
            />
            Show signals
          </label>
          <div className="relative">
            <button
              type="button"
              onClick={() => setSignalMenuOpen((v) => !v)}
              className="inline-flex items-center gap-1.5 rounded-md border border-sky-900/40 bg-[#06101e] hover:bg-[#0b1426] px-2.5 py-1 text-[12px] text-neutral-200"
            >
              {active.label}
              <ChevronDown className="h-3 w-3 text-neutral-500" />
            </button>
            {signalMenuOpen && (
              <div className="absolute right-0 mt-1.5 w-32 rounded-md border border-sky-900/40 bg-[#06101e] shadow-[0_10px_28px_-10px_rgba(0,0,0,0.8)] z-30">
                {SIGNALS.map((s) => (
                  <button
                    key={s.key}
                    type="button"
                    onClick={() => { setSignal(s.key); setSignalMenuOpen(false); }}
                    className={`flex w-full items-center justify-between px-3 py-1.5 text-left text-[12.5px] border-b border-sky-900/20 last:border-b-0 ${
                      signal === s.key ? "bg-sky-500/10 text-sky-100" : "text-neutral-300 hover:bg-white/[0.04]"
                    }`}
                  >
                    {s.label}
                    <span className="h-2 w-2 rounded-full" style={{ background: s.color }} />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Body grows to fill remaining card height so the panel matches the
          three stacked risk cards on the right. The chart container flexes
          (with a min-height) so its area scales with the freed space. */}
      <div className="px-4 py-4 grid grid-cols-1 md:grid-cols-[160px_1fr] gap-4 items-center flex-1 min-h-0">
        <div className="flex flex-col items-center justify-center">
          <Gauge score={signalScore} size="lg" />
          <div className="mt-2 text-center">
            <div className="text-[12px] text-neutral-200 font-semibold">{active.label} Threat Index</div>
            <div className="text-[11px] text-neutral-500 leading-snug mt-1 max-w-[180px]">
              Combined indicator of threats facing your organization from your partner companies.
            </div>
          </div>
        </div>
        <div className="w-full h-full min-h-[240px]">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data.trend} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="2 4" stroke="rgba(148,163,184,0.08)" />
              <XAxis dataKey="month" stroke="rgba(148,163,184,0.5)" fontSize={10} tickLine={false} axisLine={{ stroke: "rgba(148,163,184,0.15)" }} />
              <YAxis stroke="rgba(148,163,184,0.5)" fontSize={10} tickLine={false} axisLine={{ stroke: "rgba(148,163,184,0.15)" }} domain={[0, 100]} />
              <Tooltip
                cursor={{ stroke: "rgba(148,163,184,0.2)" }}
                contentStyle={{ background: "#0a1224", border: "1px solid rgba(56,189,248,0.3)", borderRadius: 6, fontSize: 12 }}
              />
              {signalsOn && (
                <Line type="monotone" dataKey={signal} stroke={active.color} strokeWidth={2} dot={{ r: 2.5, fill: active.color }} activeDot={{ r: 4 }} />
              )}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </section>
  );
}

function RiskCategoryPanel({ title, score, totalSources, mostRecentLabel, mostRecentDate, frequency, stats }) {
  return (
    <section className={`${panel} px-4 py-4 flex items-start gap-4`}>
      <Gauge score={score} size="md" label={`${title} Index`} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="text-[15px] font-semibold text-neutral-100">{title}</h3>
          <span className="inline-flex items-center rounded-full border border-sky-900/40 bg-sky-500/10 px-2 py-0.5 text-[10.5px] text-sky-200">
            {totalSources.toLocaleString()} Total Sources
          </span>
        </div>
        <div className="mt-1 text-[12px] text-neutral-400">
          {mostRecentLabel}: <span className="text-neutral-200">{mostRecentDate}</span>
          <span className="mx-2 text-neutral-700">•</span>
          Frequency: <span className="text-neutral-200">{frequency.toLocaleString()}/month</span>
        </div>
        <div className="mt-3 grid grid-cols-3 gap-3">
          {stats.map((s) => (
            <div key={s.label}>
              <div className="text-[11px] text-neutral-500">{s.label}</div>
              <div className="mt-0.5 flex items-center gap-1.5">
                <span className="text-[15px] font-semibold text-neutral-100 tabular-nums">
                  {s.value.toLocaleString()}
                </span>
                {s.trend === "up" && <ArrowUpRight className="h-3.5 w-3.5 text-rose-400" />}
                {s.trend === "down" && <ArrowDownRight className="h-3.5 w-3.5 text-emerald-400" />}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function EventsPanel({ data }) {
  return (
    <section className={panel}>
      <div className={panelHeader}>
        <div className={panelTitle}>Recent Identity Exposure Events</div>
        <span className="inline-flex items-center rounded-full border border-sky-900/40 bg-sky-500/10 px-2 py-0.5 text-[10.5px] text-sky-200">
          {data.totalSources.toLocaleString()} Total Sources
        </span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[12.5px]">
          <thead className="bg-white/[0.02] text-neutral-400">
            <tr>
              <th className="px-4 py-2.5 text-left font-medium uppercase tracking-wider text-[11px]">Source</th>
              <th className="px-4 py-2.5 text-left font-medium uppercase tracking-wider text-[11px]">Source Type</th>
              <th className="px-4 py-2.5 text-left font-medium uppercase tracking-wider text-[11px]">Date</th>
              <th className="px-4 py-2.5 text-right font-medium uppercase tracking-wider text-[11px]"># of Employees</th>
            </tr>
          </thead>
          <tbody>
            {data.recentEvents.map((e, i) => (
              <tr key={i} className={`border-t border-sky-900/20 hover:bg-sky-900/15 ${i % 2 === 0 ? "" : "bg-[#091020]/30"}`}>
                <td className="px-4 py-2.5">
                  <div className="flex items-center gap-2">
                    <CompanyAvatar name={e.source} size={20} />
                    <a href="#" className="text-sky-300 hover:text-sky-200 underline-offset-2 hover:underline">
                      {e.source}
                    </a>
                  </div>
                </td>
                <td className="px-4 py-2.5 text-neutral-300">{e.type}</td>
                <td className="px-4 py-2.5 text-neutral-300 tabular-nums">{e.date}</td>
                <td className="px-4 py-2.5 text-right text-neutral-100 font-medium tabular-nums">{e.employees}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function ReusedColumn({ rows }) {
  return (
    <div>
      <div className="grid grid-cols-[1fr_auto_auto] gap-x-4 text-[11px] uppercase tracking-wider text-neutral-500 mb-1.5">
        <span />
        <span className="text-right">Sightings</span>
        <span className="text-right">Users</span>
      </div>
      {rows.map((row, i) => (
        <div key={i} className="grid grid-cols-[1fr_auto_auto] gap-x-4 py-1 text-[12.5px] border-b border-sky-900/15 last:border-b-0">
          <span className="text-neutral-400 tracking-widest">{row.masked}</span>
          <span className="text-right text-neutral-100 tabular-nums">{row.sightings}</span>
          <span className="text-right text-neutral-100 tabular-nums">{row.users}</span>
        </div>
      ))}
    </div>
  );
}

function PasswordReusePanel({ data }) {
  const { percent, leftColumn, rightColumn } = data.passwordReuse;
  return (
    <section className={panel}>
      <div className={panelHeader}>
        <div className="flex items-center gap-1.5">
          <span className={panelTitle}>Password Reuse</span>
          <Info className="h-3.5 w-3.5 text-neutral-500" />
        </div>
      </div>
      <div className="px-4 py-4">
        <div className="flex items-center gap-3">
          <span className="text-[15px] font-semibold text-neutral-100 tabular-nums">{percent}%</span>
          <div className="flex-1 h-2 rounded-full bg-neutral-800 overflow-hidden">
            <div
              className="h-full"
              style={{
                width: `${percent}%`,
                background: "linear-gradient(90deg, #10b981 0%, #eab308 45%, #f59e0b 70%, #f43f5e 100%)",
              }}
            />
          </div>
        </div>
        <div className="mt-5">
          <div className="text-[11px] uppercase tracking-wider text-neutral-500 mb-2">Top Reused Passwords</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1.5">
            <ReusedColumn rows={leftColumn} />
            <ReusedColumn rows={rightColumn} />
          </div>
        </div>
      </div>
    </section>
  );
}

function AppColumn({ title, rows }) {
  return (
    <div>
      <div className="flex items-center justify-between text-[11px] uppercase tracking-wider text-neutral-500 pb-1.5 border-b border-sky-900/30">
        <span>{title}</span>
        <span>Infected Devices</span>
      </div>
      {rows.map((row, i) => (
        <div key={i} className="flex items-center justify-between gap-3 py-1.5 text-[12.5px] border-b border-sky-900/15 last:border-b-0">
          <span className="text-sky-300 truncate min-w-0">{row.name}</span>
          <span className="text-neutral-100 tabular-nums">{row.infected}</span>
        </div>
      ))}
    </div>
  );
}

function TopAppsPanel({ data }) {
  return (
    <section className={panel}>
      <div className={panelHeader}>
        <div className={panelTitle}>Top Compromised Applications</div>
        <span className="inline-flex items-center rounded-full border border-sky-900/40 bg-sky-500/10 px-2 py-0.5 text-[10.5px] text-sky-200">
          {data.malware.compromisedApps.toLocaleString()} Total
        </span>
      </div>
      <div className="px-4 py-4 grid grid-cols-1 md:grid-cols-2 gap-6">
        <AppColumn title="Internal Applications" rows={data.topCompromisedApps.internal} />
        <AppColumn title="External Applications" rows={data.topCompromisedApps.external} />
      </div>
    </section>
  );
}

/* ────────────────────────── Page ────────────────────────── */

const RANGE_OPTIONS = [
  { key: "3m", label: "Last 3 Months" },
  { key: "6m", label: "Last 6 Months" },
  { key: "12m", label: "Last 12 Months" },
  { key: "24m", label: "Last 24 Months" },
];

export default function SupplyChainDetail() {
  const navigate = useNavigate();
  const { domain } = useParams();
  const [collapsed, setCollapsed] = useSidebarCollapsed();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [rangeKey, setRangeKey] = useState("12m");
  const [rangeOpen, setRangeOpen] = useState(false);
  const range = RANGE_OPTIONS.find((r) => r.key === rangeKey);

  const decoded = decodeURIComponent(domain || "");
  const company = useMemo(() => {
    const all = loadCompanies();
    return all.find((c) => c.domain === decoded);
  }, [decoded]);

  const data = useMemo(() => (company ? generateDetailData(company) : null), [company]);

  if (!company || !data) {
    return (
      <div className="min-h-screen bg-[#000414] text-white">
        <div
          className="bg-[#0a1224]/85 border-b border-sky-800/40 backdrop-blur sticky top-0 z-50"
          style={{ marginLeft: collapsed ? "56px" : "224px" }}
        >
          <div className="px-6 h-10 flex items-center">
            <button
              type="button"
              onClick={() => navigate("/supply-chain")}
              className="inline-flex items-center gap-1.5 text-[12px] text-neutral-400 hover:text-sky-300 transition-colors"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Back
            </button>
          </div>
        </div>
        <div className="flex">
          <Sidebar
            collapsed={collapsed}
            setCollapsed={setCollapsed}
            mobileOpen={mobileOpen}
            setMobileOpen={setMobileOpen}
            activeKey="supplychain"
            onChange={() => {}}
          />
          <main className="flex-1 px-6 py-10">
            <div className={`${panel} px-6 py-10 text-center max-w-md mx-auto`}>
              <p className="text-[14px] text-neutral-300">Vendor not found.</p>
              <p className="mt-1 text-[12px] text-neutral-500 mb-4">
                <code className="text-sky-300">{decoded}</code> is not in your monitored supply chain.
              </p>
              <button
                type="button"
                onClick={() => navigate("/supply-chain")}
                className="inline-flex items-center gap-1.5 rounded-md border border-sky-600/40 bg-sky-500/15 hover:bg-sky-500/25 px-3 py-1.5 text-[13px] font-medium text-sky-100"
              >
                Back to Supply Chain
              </button>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#000414] text-white">
      {/* ───── Top: thin back bar (own row, above the header) ───── */}
      <div
        className="bg-[#0a1224]/85 border-b border-sky-800/40 backdrop-blur sticky top-0 z-50"
        style={{ marginLeft: collapsed ? "56px" : "224px" }}
      >
        <div className="px-6 h-10 flex items-center">
          <button
            type="button"
            onClick={() => navigate("/supply-chain")}
            className="inline-flex items-center gap-1.5 text-[12px] text-neutral-400 hover:text-sky-300 transition-colors"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Back
          </button>
        </div>
      </div>

      {/* ───── Supplier header ───── */}
      <header
        className="bg-[#0a1224]/85 border-b border-sky-800/40 backdrop-blur shadow-[0_6px_16px_-8px_rgba(0,0,0,0.7)]"
        style={{ marginLeft: collapsed ? "56px" : "224px" }}
      >
        <div className="px-6 py-4 flex items-start justify-between gap-4 flex-wrap">
          {/* Left: avatar + name + meta */}
          <div className="flex items-start gap-3 min-w-0">
            <CompanyAvatar name={data.name} size={56} />
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-[22px] font-bold leading-tight tracking-tight text-neutral-100">
                  {data.name}
                </h1>
                <Gauge score={data.identity} size="sm" />
              </div>
              <div className="mt-1.5 flex items-center gap-x-3 gap-y-1 flex-wrap text-neutral-300">
                <MetaItem label="Industry" value={data.industry} />
                <span className="text-neutral-700">|</span>
                <MetaItem label="Size" value={data.size} />
                <span className="text-neutral-700">|</span>
                <MetaItem
                  label="HQ Location"
                  value={data.hqLocation}
                  valueExtra={
                    <span className="text-[14px] leading-none">{FLAG[data.country] || ""}</span>
                  }
                />
                <span className="text-neutral-700">|</span>
                <MetaItem label="Domains" value={data.domainsCount} />
              </div>
            </div>
          </div>

          {/* Right: range select + most recent sighting */}
          <div className="flex flex-col items-end gap-1.5">
            <div className="relative">
              <button
                type="button"
                onClick={() => setRangeOpen((v) => !v)}
                className="inline-flex items-center gap-1.5 rounded-md border border-sky-900/40 bg-[#06101e] hover:bg-[#0b1426] px-2.5 py-1.5 text-[12px] text-neutral-200"
              >
                {range.label}
                <ChevronDown className="h-3 w-3 text-neutral-500" />
              </button>
              {rangeOpen && (
                <div className="absolute right-0 mt-1.5 w-44 rounded-md border border-sky-900/40 bg-[#06101e] shadow-[0_10px_28px_-10px_rgba(0,0,0,0.8)] z-30">
                  {RANGE_OPTIONS.map((r) => (
                    <button
                      key={r.key}
                      type="button"
                      onClick={() => { setRangeKey(r.key); setRangeOpen(false); }}
                      className={`block w-full px-3 py-1.5 text-left text-[12.5px] border-b border-sky-900/20 last:border-b-0 ${
                        rangeKey === r.key ? "bg-sky-500/10 text-sky-100" : "text-neutral-300 hover:bg-white/[0.04]"
                      }`}
                    >
                      {r.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="text-[12px]">
              <span className="text-neutral-500">Most Recent Sighting: </span>
              <span className="text-neutral-200 font-medium tabular-nums">{data.mostRecentSighting}</span>
            </div>
          </div>
        </div>
      </header>

      {/* ───── Body ───── */}
      <div className="flex">
        <Sidebar
          collapsed={collapsed}
          setCollapsed={setCollapsed}
          mobileOpen={mobileOpen}
          setMobileOpen={setMobileOpen}
          activeKey="supplychain"
          onChange={() => {}}
        />

        <main className="flex-1 min-w-0 px-4 md:px-6 pt-4 pb-10">
          {/* Top row */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
            <div className="lg:col-span-5">
              <IdentityThreatPanel data={data} />
            </div>
            <div className="lg:col-span-7 space-y-3">
              <RiskCategoryPanel
                title="Malware"
                score={data.malware.index}
                totalSources={data.malware.totalSources}
                mostRecentLabel="Most Recent Infection"
                mostRecentDate={data.malware.mostRecentInfection}
                frequency={data.malware.frequency}
                stats={[
                  { label: "Infected Devices", value: data.malware.infectedDevices, trend: "up" },
                  { label: "Infected Employees", value: data.malware.infectedEmployees, trend: "up" },
                  { label: "Compromised Applications", value: data.malware.compromisedApps, trend: "up" },
                ]}
              />
              <RiskCategoryPanel
                title="Breaches"
                score={data.breaches.index}
                totalSources={data.breaches.totalSources}
                mostRecentLabel="Most Recent Exposure"
                mostRecentDate={data.breaches.mostRecentExposure}
                frequency={data.breaches.frequency}
                stats={[
                  { label: "Exposed Records", value: data.breaches.exposedRecords, trend: "down" },
                  { label: "Exposed Employees", value: data.breaches.exposedEmployees, trend: "down" },
                  { label: "Plaintext Passwords", value: data.breaches.plaintextPasswords, trend: "up" },
                ]}
              />
              <RiskCategoryPanel
                title="Combolists"
                score={data.combolists.index}
                totalSources={data.combolists.totalSources}
                mostRecentLabel="Most Recent Exposure"
                mostRecentDate={data.combolists.mostRecentExposure}
                frequency={data.combolists.frequency}
                stats={[
                  { label: "Exposed Records", value: data.combolists.exposedRecords, trend: "up" },
                  { label: "Exposed Employees", value: data.combolists.exposedEmployees, trend: "up" },
                  { label: "Plaintext Passwords", value: data.combolists.plaintextPasswords, trend: "up" },
                ]}
              />
            </div>
          </div>

          {/* Bottom row */}
          <div className="mt-4 grid grid-cols-1 lg:grid-cols-12 gap-4">
            <div className="lg:col-span-5 space-y-4">
              <EventsPanel data={data} />
              <TopAppsPanel data={data} />
            </div>
            <div className="lg:col-span-7">
              <PasswordReusePanel data={data} />
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
